import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useToast } from '../../context/ToastContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Logo from '../Logo';

const StudioLoginPage: React.FC = () => {
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResetMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (error) throw error;
      setResetMessage('Check your email for the password reset link!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-fade-in">
      <Link
        to="/"
        className="mb-8 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-teal-600 transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
        Back to Home
      </Link>

      <div className="bg-white p-8 lg:p-10 rounded-[2.5rem] shadow-2xl border border-gray-100">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            {isForgotPassword ? 'Reset Password' : 'Studio Login'}
          </h2>
          <p className="text-gray-500">
            {isForgotPassword
              ? 'Enter your email to receive a reset link.'
              : 'Sign in to access your video creator studio.'}
          </p>
        </div>

        {resetMessage && (
          <div className="mb-6 p-4 bg-teal-50 text-teal-600 rounded-xl text-sm font-bold text-center">
            {resetMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold text-center">
            {error}
          </div>
        )}

        <form onSubmit={isForgotPassword ? handleForgotPassword : handleAuth} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-bold text-gray-700">Email Address</label>
            <input
              required
              type="email"
              placeholder="name@example.com"
              className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition-all"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {!isForgotPassword && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-bold text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setError(null);
                    setResetMessage(null);
                  }}
                  className="text-xs font-bold text-teal-600 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <input
                required
                type="password"
                placeholder="••••••••"
                className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-5 bg-teal-600 text-white rounded-full text-lg font-bold hover:bg-teal-700 transition-all shadow-lg hover:shadow-teal-500/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (isForgotPassword ? 'Send Reset Link' : 'Sign In')}
          </button>

          {isForgotPassword && (
            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setError(null);
                  setResetMessage(null);
                }}
                className="text-gray-500 text-sm hover:text-teal-600 transition-colors"
              >
                Back to Login
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default StudioLoginPage;
